import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonLayout from '../../../layouts/Common';
import CommonTable from '../../../components/Common/Table';
import { ActionBtn, HeaderData, NameData, UrlData } from './style';
import { ColumnsType } from 'antd/es/table';
import { DataType } from './type';
import HRVForm from '../../../components/Forms/Admin/HRV';
import useListAnalysesView, { useToggleProfessionalViewable } from '../../../hooks/admin/useListAnalysesView';
import useGetAllProfessionals from '../../../hooks/professionals/useGetAllProfessionals';
import { formatDate, getNumericId } from '../../../helpers';
import { Spin, App } from 'antd';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { ADMIN_TOGGLE_CLIENT_VIEWABLE } from '../../../services/graphql/mutations/admin';
import { useAdminSignInAsProfessional } from '../../../hooks/admin/useAdminProfessional';

const HRV = () => {
  const token = useSelector((state: any) => state.auth.token);
  const professionals = useSelector((state: any) => state.professionals.professionals || []);
  const [toggleClientMutate] = useMutation(ADMIN_TOGGLE_CLIENT_VIEWABLE);
  const { signInAsProfessional } = useAdminSignInAsProfessional();
  const { message } = App.useApp();

  useGetAllProfessionals(); // Fetch professionals for ID lookup
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { data: hrvData, total, loading, perPage, refetch } = useListAnalysesView('hrv', currentPage as number);
  const { toggle, loading: toggleLoading } = useToggleProfessionalViewable();
  const [openNewHRV, setOpenNewHRV] = useState<boolean>(false);
  const navigate = useNavigate();

  const onNavigate = (record: any) => {
    navigate(`/a/hrv_reports/${record.analysesId || record.id}`, {
      state: {
        payload: record
      }
    });
  };

  const onToggle = async (record: any) => {
    const analysesId = getNumericId(record.analysesId || record.id);
    const profCode = record.professional?.code || record.professionalCode;
    const selectedProf = professionals?.find((p: any) => p.code === profCode);
    const professionalId = getNumericId(record.professional?.id || record.professionalId || selectedProf?.id);
    const clientIdRaw = record.client?.id || record.clientId || record.clientGid || record.clientGidId;
    const clientId = getNumericId(clientIdRaw);

    if (!analysesId || !professionalId || !clientId) {
      message.error('Selection metadata missing.');
      return;
    }

    try {
      // Toggle Professional Viewable
      await toggle({
        analysesId: analysesId,
        analysesType: 'hrv',
        professionalId: professionalId
      });

      // Toggle Client Viewable (Syncing for Professional dashboard visibility)
      // Admins are naturally restricted from this mutation, so we assume the Professional's role
      if (selectedProf?.email) {
        try {
          const proSignIn = await signInAsProfessional(selectedProf.email);
          if (proSignIn?.token) {
            await toggleClientMutate({
              variables: {
                input: {
                  analysesId: analysesId,
                  analysesType: 'hrv',
                  clientId: record.clientId || clientId.toString()
                }
              },
              context: {
                headers: { Authorization: `Bearer ${proSignIn.token}` }
              }
            });
          }
        } catch (syncErr) {
          console.warn('Sync toggleClientViewable failed: ', syncErr);
        }
      }

      await refetch();
      message.success('Visibility updated successfully');
    } catch (err: any) {
      console.error('onToggle Error: ', err);
      message.error(`Failed to update visibility: ${err?.message || 'Error'}`);
    }
  }

  const columns: ColumnsType<any> = [
    {
      title: <HeaderData>Client</HeaderData>,
      render: (text, record) => (
        <NameData>{`${record.client || ''}`}</NameData>
      ),
    },
    {
      title: 'GID',
      render: (text, record) => (
        <NameData>{`${record.clientGidId || record.clientGid || ''}`}</NameData>
      ),
    },
    {
      title: <HeaderData>Professional</HeaderData>,
      render: (text, record) => (
        <NameData>{`${record.professional || ''}`}</NameData>
      ),
    },
    {
      title: 'Code',
      render: (text, record) => (
        <NameData>{`${record.professional?.code || record.professionalCode || ''}`}</NameData>
      ),
    },
    {
      title: <HeaderData>Added by</HeaderData>,
      render: (text, record) => (
        <NameData>{`${record.addedBy || 'Admin'}`}</NameData>
      )
    },
    {
      title: <HeaderData>Date</HeaderData>,
      render: (text, record) => (
        <UrlData>{`${formatDate(record.dateOfAnalysis || record.date || record.createdAt)}`}</UrlData>
      ),
    },
    {
      title: <HeaderData>Actions</HeaderData>,
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <ActionBtn
            onClick={() => onNavigate(record)}
            $active={false}
            type="text"
            icon={
              <i className="material-icons-round" style={{ fontSize: 'inherit' }}>visibility</i>
            }
          >
            View
          </ActionBtn>
          <ActionBtn
            onClick={() => onToggle(record)}
            $active={record.professionalViewable}
            type="text"
            icon={
              <i className="material-icons-outlined" style={{ fontSize: 'inherit' }}>policy</i>
            }
          >
            {record.professionalViewable ? 'Disable' : 'Enable'}
          </ActionBtn>
        </div>
      ),
    }
  ];

  const handleBack = () => {
    setOpenNewHRV(false);
  };

  return (
    <CommonLayout title="HRV" width="1000px">
      {loading ? <Spin size="large" /> :
        <CommonTable
          showNewModal={openNewHRV}
          onNewData={setOpenNewHRV}
          showEditModal={false}
          onEditData={() => {}}
          handleBack={handleBack}
          modalForm={
            <HRVForm
              onSuccess={async () => {
                try {
                  await refetch();
                  setOpenNewHRV(false);
                } catch (error) {
                  console.error('HRV Form onSuccess - Refetch error:', error);
                }
              }}
            />
          }
          modalWidth={1200}
          columns={columns}
          dataSource={hrvData}
          tableType="HRV"
          total={`${total} rows`}
          page={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          totalData={total}
        />
      }
    </CommonLayout>
  )
};

export default HRV;